import { Box, Button } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import DatePicker from 'components/date-picker';
import NumberField from 'components/number-field';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import dayjs, { Dayjs } from 'dayjs';
import { Fish, Shrimp } from 'lucide-react';
import { useState } from 'react';

const LONG_VALUE = 'Test Value Long Name Extends Past Width';
const HELPER_TEXT = 'This is helper text';

// Each row shows one component in these states, in this order.
const VARIANTS = [
  { label: 'Light', dark: false, disabled: false, error: false },
  { label: 'Dark', dark: true, disabled: false, error: false },
  { label: 'Disabled', dark: false, disabled: true, error: false },
  { label: 'Error', dark: false, disabled: false, error: true },
];

// The standard Paper also demonstrates the Select's toggle, action and search slots.
const SURFACES = [
  { id: 'transparent', transparent: true, extendedSelect: false },
  { id: 'standard', transparent: false, extendedSelect: true },
];

const ComponentPalette = () => {
  const [textfieldValue, setTextfieldValue] = useState<string>(LONG_VALUE);
  const [textfieldNumberValue, setTextfieldNumberValue] = useState<string>('0');
  const [toggle, setToggle] = useState<boolean>(false);
  const [autocompleteValue, setAutocompleteValue] = useState<string>(LONG_VALUE);
  const [datePickerValue, setDatePickerValue] = useState<Dayjs>(dayjs);
  const [selectValue, setSelectValue] = useState<string>('0');

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {SURFACES.map((surface) => (
        <Paper key={surface.id} transparent={surface.transparent}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-around"
            gap={4}
            padding={surface.transparent ? undefined : 4}
          >
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant, i) => (
                <TextField
                  key={variant.label}
                  fullWidth
                  startAdornment={<Shrimp />}
                  endAdornment={<Fish />}
                  value={textfieldValue}
                  onChange={(e) => setTextfieldValue(e.target.value as string)}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  helperText={HELPER_TEXT}
                  label={`TextField ${i + 1} (${variant.label})`}
                />
              ))}
            </Box>
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant, i) => (
                <NumberField
                  key={variant.label}
                  fullWidth
                  startAdornment={<Shrimp />}
                  endAdornment={<Fish />}
                  value={textfieldNumberValue}
                  onChange={(e) => setTextfieldNumberValue(e.target.value as string)}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  helperText={HELPER_TEXT}
                  label={`NumberField ${i + 1} (${variant.label})`}
                />
              ))}
            </Box>
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant) => (
                <Select
                  key={variant.label}
                  fullWidth
                  {...(surface.extendedSelect && {
                    toggle: { value: toggle, onChange: setToggle, label: 'Toggle' },
                    action: <Switch dark label="Action toggle" value={toggle} onChange={() => setToggle((prev) => !prev)} />,
                    search: { value: textfieldValue, onChange: setTextfieldValue },
                  })}
                  startAdornment={<Shrimp />}
                  value={selectValue}
                  onChange={(e) => setSelectValue(e.target.value as string)}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  helperText={HELPER_TEXT}
                  label={`Select (${variant.label})`}
                >
                  <RadioSelectItem value="0">{LONG_VALUE}</RadioSelectItem>
                </Select>
              ))}
            </Box>
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant, i) => (
                <DatePicker
                  key={variant.label}
                  fullWidth
                  value={datePickerValue}
                  onChange={(e) => setDatePickerValue(e)}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  helperText={HELPER_TEXT}
                  label={`DatePicker ${i + 1} (${variant.label})`}
                />
              ))}
            </Box>
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant, i) => (
                <Autocomplete
                  key={variant.label}
                  fullWidth
                  onChange={(_e, value) => setAutocompleteValue(value as string)}
                  value={autocompleteValue}
                  options={[LONG_VALUE, '1']}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  helperText={HELPER_TEXT}
                  label={`Autocomplete ${i + 1} (${variant.label})`}
                />
              ))}
            </Box>
            <Box display="flex" gap={4}>
              {VARIANTS.map((variant, i) => (
                <Switch
                  key={variant.label}
                  fullWidth
                  value={toggle}
                  onChange={() => setToggle((prev) => !prev)}
                  dark={variant.dark}
                  disabled={variant.disabled}
                  error={variant.error}
                  label={`Switch ${i + 1} (${variant.label})`}
                  helperText={HELPER_TEXT}
                />
              ))}
            </Box>
            <Box display="flex" gap={4}>
              <Button fullWidth={!surface.transparent}>{surface.transparent ? 'Button' : 'Button (Full width)'}</Button>
              <Button disabled fullWidth={!surface.transparent}>
                {surface.transparent ? 'Button (Disabled)' : 'Button (Disabled, Full width)'}
              </Button>
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default ComponentPalette;
